import { Feature } from '@account-users/account/models';
import { getHasFeatureLicense } from '@account-users/account/state/index';
import { FeatureToggleStateService } from '@account-users/account/state/feature-toggles/feature-toggle-state.service';
import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { FolderItemType, OneApiService } from '@shared';
import { ToastrService } from 'ngx-toastr';
import {
  map,
  catchError,
  concatMap,
  filter,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

import { TemplateActions } from '../actions';
import { EmailService } from '../services';
import { State } from '../reducers';

@Injectable()
export class TemplateEffects {
  fetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.fetchRequest),
      concatMap(({ accountId }) =>
        this.service.fetchTemplates(accountId).pipe(
          map((templates) => TemplateActions.fetchSuccess({ templates })),
          catchError(() => {
            this.toastr.error('Could not fetch templates');
            return [TemplateActions.fetchFailure()];
          }),
        ),
      ),
    ),
  );

  copy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.copyRequest),
      tap(({ toAccountId }) =>
        this.featureToggleStateService.loadLicenses(toAccountId),
      ),
      concatMap(({ templateIds, toAccountId, fromAccountId }) => {
        return forkJoin(
          templateIds.map((id) =>
            this.service.copyTemplate(id, toAccountId, fromAccountId),
          ),
        ).pipe(
          switchMap((res) =>
            this.featureToggleStateService.selectLicesesIsLoading().pipe(
              filter((isLoading) => !isLoading),
              withLatestFrom(
                this.store.pipe(select(getHasFeatureLicense(Feature.Folders))),
              ),
              map(([_, hasFoldersFeatureEnabled]) => ({
                res,
                hasFoldersFeatureEnabled,
              })),
              take(1),
            ),
          ),
          switchMap(({ res, hasFoldersFeatureEnabled }) => {
            if (hasFoldersFeatureEnabled) {
              return this.oneApiService
                .itemsInRootFolder(
                  toAccountId,
                  FolderItemType['email-template'],
                  (res as { id: string }[]).map(({ id }) => id),
                )
                .pipe(
                  map(() => {
                    this.toastr.success(
                      `${templateIds.length} template(s) successfully copied!`,
                    );
                    return TemplateActions.copySuccess();
                  }),
                );
            }
            this.toastr.success(
              `${templateIds.length} template(s) successfully copied!`,
            );
            return of(TemplateActions.copySuccess());
          }),
          catchError(() => {
            this.toastr.error('Could not copy templates');
            return [TemplateActions.copyFailure()];
          }),
        );
      }),
    ),
  );

  reBootstrap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TemplateActions.reBootstrapRequest),
      concatMap(({ accountId }) => {
        const service = accountId
          ? this.service.reBootstrapByAccount(accountId)
          : this.service.reBootstrapAllAccounts();

        return service.pipe(
          map(() => {
            this.toastr.success(
              `Re-bootstrapping on ${
                accountId ? accountId : 'all accounts'
              } started...`,
            );
            return TemplateActions.reBootstrapSuccess();
          }),
          catchError(() => {
            this.toastr.error(
              `Could not start re-bootstrap on ${
                accountId ? accountId : 'all accounts'
              }`,
            );
            return [TemplateActions.reBootstrapFailure()];
          }),
        );
      }),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly service: EmailService,
    private readonly toastr: ToastrService,
    private readonly store: Store<State>,
    private readonly oneApiService: OneApiService,
    private readonly featureToggleStateService: FeatureToggleStateService,
  ) {}
}
